/* =====================================================================================
   Atlas seed data.

   Real figures from the research pass of 2 August 2026. Italy is verified centre-level
   data from the Centro Nazionale Trapianti Report 2024. UK structural data is from NHSBT
   INF1466/3.1 and the UKAS/EFI schedules.

   This module exists so the interface runs and demonstrates against real content before
   Supabase is provisioned. In production these shapes are returned by SQL views; the
   component layer does not change.
   ===================================================================================== */

import { GRAFT_YEARS } from './atlas.core';
export { GRAFT_YEARS, organLabel, siteTotal } from './atlas.core';

export type Confidence = 'verified' | 'derived' | 'estimated' | 'unverified';
/* The reported categories, which are not the same as the programmes a centre runs. Registries
   publish combined figures and splitting them would be fabrication: NHSBT reports its four
   thoracic units as one 'cardiothoracic' total, and a simultaneous kidney-pancreas is one
   procedure producing two grafts. This type previously omitted the three combined categories
   even though the data has always contained them. */
export type Organ =
  | 'kidney' | 'liver' | 'heart' | 'lung' | 'pancreas' | 'intestine'
  | 'kidney_pancreas' | 'heart_lung' | 'cardiothoracic'
  | 'hsct_allo' | 'hsct_auto';

/* What a market is segmented BY, in shared code that must not care.
   Deliberately a bare string. `Organ` above is Scira's own set of values and its data files
   still declare against it, so the transplant seed keeps its exhaustiveness checking; a tenant
   segmenting by joint replaced, or by tumour type, or not at all, is equally well typed here.
   The field below is still called `organ` because renaming it would touch every module for no
   behavioural gain, and the name is internal. */
export type SegmentKey = string;

export interface Volume {
  organ: SegmentKey;
  year: number;
  total: number;
  living?: number;
  confidence: Confidence;
  /* The market this count was made in, as the market's code. Present only for a tenant that
     runs more than one market (the export omits it otherwise): a market is a lens, and the same
     centre carries a different count under each. Atlas keeps only the selected market's volumes
     when the selector is set. */
  market?: string;
}

export interface Site {
  id: string;
  name: string;
  short: string;
  city: string;
  /* OPTIONAL. Most countries in the model have no meaningful sub-national unit for a hospital,
     and the previous seed stored an empty string for all of them, which the type then described
     as a region that happens to be blank. Every consumer already treats it as absent: db.ts
     filters it out of the subtitle, Atlas guards it in the tooltip, SitePage filters it from the
     breadcrumb. The type is being corrected to match code that was always written this way. */
  region?: string;
  iso3: string;
  /* OPTIONAL, and the code has always assumed so. Atlas filters on Number.isFinite before
     projecting, the map reports how many sites it left off, and this file's own header says
     coordinate-less centres are kept rather than dropped. Every one of the 1,334 exported
     centres happened to have coordinates, so the type was never contradicted; the 138 United
     States laboratories include 36 in cities with no mapped centre, and inventing positions for
     them to satisfy a type would be the wrong way round. */
  lat?: number;
  lon?: number;
  /** 'city' = placed from the gazetteer. 'approx' = country centroid fallback. */
  geo?: 'city' | 'approx' | null;
  source?: string | null;
  /* Codes from atlas.site_kinds, which since migration 0083 is a lookup table the tenant may
     extend rather than a CHECK listing one industry's vocabulary. The three transplant kinds
     this used to enumerate are still valid values; they are no longer the only ones. */
  kinds: string[];
  paediatric?: boolean;
  /* The markets this site belongs to (atlas.site_markets), as codes. Present only for a
     multi-market tenant. Absent means "every market", which is also what a single-market
     tenant's sites are. */
  markets?: string[];
  volumes: Volume[];
  /** Prevalent followed grafts. The addressable base for surveillance, not incident volume. */
  prevalentGrafts?: { value: number; confidence: Confidence; method: string };
  /** Per-organ split of prevalentGrafts. The database stores prevalence per organ; the
      previous seed filed every value under 'kidney', which was wrong for liver and
      thoracic centres. */
  prevalentByOrgan?: Record<string, number>;
  waitingList?: number;
  /** @deprecated Populated for 8 of 617 sites (all Italian), so it could never be
      compared across centres. Being replaced by active waiting list. */
  hypersensitised?: number;
  dcdListed?: number;
  dualKidney?: number;
  /** Tier 1/2/3 throughout, matching Ariadne Thread's KOL tiering. Never a second scale. */
  tier: 1 | 2 | 3;
  incumbent?: string[];
  note?: string | null;
  labNote?: string;
}

/*
 * A national waiting list.
 *
 * Held per organ with its basis and date attached rather than as a bare number, because a
 * bare number here is not interpretable and is easy to misuse:
 *
 *   BASIS. Registries do not count the same thing. NHSBT reports an active list and reports
 *   suspended patients separately. OPTN's headline candidate count INCLUDES inactive
 *   candidates who cannot receive an offer, and is materially larger than its active subset.
 *   Adding one country's active list to another's total produces a figure that means nothing,
 *   so the basis travels with the number and the interface refuses to mix the two silently.
 *
 *   DATE. A waiting list is a stock at an instant, not a flow over a year. Two countries
 *   measured six months apart are not comparable, and a list published annually goes stale
 *   in a way an annual transplant count does not.
 */
export interface WaitingList {
  kidney?: number | null;
  liver?: number | null | null;
  heart?: number | null | null;
  lung?: number | null | null;
  pancreas?: number | null;
  /** What the registry actually counted. Never assume 'active'. */
  basis: 'active' | 'total' | 'unclear';
  /** ISO date the stock refers to, not the year of publication. */
  asAt: string;
  source: string;
  sourceUrl?: string;
  confidence: Confidence;
  notes?: string;
}

export interface Country {
  iso3: string;
  iso2: string;
  name: string;
  /* NULLABLE, because for ten countries it is genuinely not established. The previous seed
     wrote 0 rather than null, which put Israel, Taiwan and Egypt in the model with a population
     of zero: a number that reads as data, sorts as data and is false. Nothing reads this field
     yet, so the only cost of telling the truth here is the type. */
  population: number | null;
  donorsPmp: number | null;
  donorsPmpYear?: number | null;
  donationModel: 'opt_in' | 'opt_out_soft' | 'opt_out_hard' | 'mixed' | 'unknown';
  /* Nullable for the same reason. "Not recorded" was being stored as the registry's NAME, so a
     country with no published registry was indistinguishable from one whose registry is called
     Not recorded. db.ts already reads this with ?? '' and always has. */
  registry: string | null;
  registryUrl?: string | null;
  /* 'excluded' is a real market position, not a gap: China is deliberately not modelled
      and saying so is the honest alternative to a silent absence. */
  priority: 'tier_1' | 'tier_2' | 'tier_3' | 'watch' | 'excluded';
  entryMode: 'direct' | 'distributor' | 'service_lab' | 'partner' | 'none';
  kidney: number | null;
  liver?: number | null;
  heart?: number | null;
  lung?: number | null;
  /* National annual volume per segment key, for tenants whose segments are not organs.
     The four fixed columns above are transplantation's shape and cannot describe a market
     segmented by knee, hip and shoulder. Where this map is present Atlas prefers it; where it
     is absent the fixed columns are read, so the transplant seed is untouched. New tenants
     should populate this and leave the fixed columns alone. */
  segments?: Record<string, number | null>;
  dataYear: number;
  confidence: Confidence;
  /* 'national_only' means the blank volumes on this country's centres are a fact about its
     registry rather than work outstanding. Six countries and 308 centres are in that position. */
  centreData?: 'per_centre_published' | 'national_only' | 'partial' | 'not_published' | 'unknown' | null;
  /** Absent means the registry publishes no waiting list we could verify, which is a real
      finding about that country and is shown as such rather than filled with an estimate. */
  waiting?: WaitingList;
  summary?: string | null;
}

export interface Pathway {
  id: string;
  iso3: string;
  assayClass: string;
  routeName: string;
  routeType:
    | 'fee_schedule' | 'drg_addon' | 'innovation_list' | 'hta_appraisal'
    | 'block_contract' | 'tender' | 'national_catalogue' | 'private_pay' | 'none';
  decisionBody: string;
  howItWorks: string;
  submissionWindow?: string;
  nextDeadline?: string;
  difficulty: 'low' | 'medium' | 'high' | 'very_high';
  status: 'not_started' | 'researching' | 'preparing' | 'submitted' | 'under_review' | 'approved' | 'rejected';
  confidence: Confidence;
  priceLow?: number;
  priceHigh?: number;
  currency?: string;
  opportunity?: string;
  /* national | sub_national | insurer | hospital, from the database. Optional: Scira's
     hand-maintained routes predate the column. */
  layer?: string;
  /* The market a route belongs to, as a code, for a multi-market tenant. Absent means the
     route applies whichever market is selected, which is the normal case: a payer's procedure
     is a property of the jurisdiction, not of the disease. */
  market?: string;
}

/* --------------------------------------------------------------------------------- */

export const COUNTRIES: Country[] = [
  {
    iso3: 'ITA', iso2: 'IT', name: 'Italy', population: 59_050_000,
    donorsPmp: 29.3, donorsPmpYear: 2024, donationModel: 'opt_out_soft',
    registry: 'Centro Nazionale Trapianti', registryUrl: 'https://www.trapianti.salute.gov.it/',
    priority: 'tier_1', entryMode: 'distributor',
    kidney: 2367, liver: 1721, heart: 413, lung: 174, dataYear: 2024, confidence: 'verified',
    summary:
      'Devolved to 20 regions, which is the dominant commercial fact: national approval does not ' +
      'produce national revenue. 86% of utilised donors are now non-standard risk, driving heavy ' +
      'pre-implantation biopsy practice and a 901-patient dual-kidney programme.',
  },
  {
    iso3: 'GBR', iso2: 'GB', name: 'United Kingdom', population: 68_300_000,
    donorsPmp: 19.2, donorsPmpYear: 2024, donationModel: 'opt_out_soft',
    registry: 'NHS Blood and Transplant', registryUrl: 'https://www.odt.nhs.uk/',
    priority: 'tier_1', entryMode: 'direct',
    kidney: 3400, liver: 900, heart: 200, lung: 180, dataYear: 2024, confidence: 'estimated',
    summary:
      'No fee schedule, which trips up US entrants. The decisive fact is Regulation 33(1)(a): an ' +
      'unconditional health institution exemption lets an NHS H&I lab run an assay with no UKCA ' +
      'or CE mark. MHRA deleted its enforcement-forbearance wording on 20 Jan 2026, so this window ' +
      'is closing.',
  },
  {
    iso3: 'USA', iso2: 'US', name: 'United States', population: 335_000_000,
    donorsPmp: 48.0, donorsPmpYear: 2024, donationModel: 'opt_in',
    registry: 'OPTN / SRTR', registryUrl: 'https://optn.transplant.hrsa.gov/',
    priority: 'tier_1', entryMode: 'service_lab',
    kidney: 28000, liver: 11000, heart: 4500, lung: 3000, dataYear: 2024, confidence: 'estimated',
    summary:
      'Where the money is, and the slowest evidence bar. CLIA plus New York CLEP, then a PLA code, ' +
      'then CLFS pricing by gapfill, then MolDX coverage. The FDA LDT rule was vacated in March 2025.',
  },
  {
    iso3: 'ESP', iso2: 'ES', name: 'Spain', population: 48_600_000,
    donorsPmp: 48.0, donorsPmpYear: 2024, donationModel: 'opt_out_soft',
    registry: 'Organización Nacional de Trasplantes', registryUrl: 'https://www.ont.es/',
    priority: 'tier_1', entryMode: 'distributor',
    kidney: 3688, liver: 1262, heart: 311, lung: 447, dataYear: 2024, confidence: 'estimated',
    summary:
      'The world leader in deceased donation for three decades. Devolved to 17 autonomous ' +
      'communities, so it is a region-by-region tender exercise. The transplant coordinator model ' +
      'means every hospital has a named, findable person in a coordination role.',
  },
  {
    iso3: 'FRA', iso2: 'FR', name: 'France', population: 68_400_000,
    donorsPmp: 28.3, donorsPmpYear: 2024, donationModel: 'opt_out_hard',
    registry: 'Agence de la biomédecine', registryUrl: 'https://www.agence-biomedecine.fr/',
    priority: 'tier_1', entryMode: 'distributor',
    kidney: 3400, liver: 1400, heart: 500, lung: 380, dataYear: 2024, confidence: 'estimated',
    summary:
      'Has the most useful early-access route in Europe: RIHN, the list of innovative ' +
      'out-of-nomenclature assays, which can generate real revenue before full HAS evaluation.',
  },
  {
    iso3: 'SAU', iso2: 'SA', name: 'Saudi Arabia', population: 33_400_000,
    donorsPmp: 4.5, donorsPmpYear: 2024, donationModel: 'opt_in',
    registry: 'Saudi Center for Organ Transplantation', registryUrl: 'https://www.scot.gov.sa/',
    priority: 'tier_2', entryMode: 'distributor',
    kidney: 1300, liver: 350, heart: 60, lung: 20, dataYear: 2024, confidence: 'estimated',
    summary:
      'Very high ESKD burden and consanguinity-related genetic renal disease, directly relevant to ' +
      'HLA typing and native disease recurrence testing. Cannot sell direct: SFDA authorisation and ' +
      'a local agent are compulsory, and NUPCO runs central procurement.',
  },
  {
    iso3: 'ARE', iso2: 'AE', name: 'United Arab Emirates', population: 10_200_000,
    donorsPmp: 6.0, donorsPmpYear: 2024, donationModel: 'opt_in',
    registry: 'Hayat / MOHAP', priority: 'tier_2', entryMode: 'distributor',
    kidney: 250, liver: 80, heart: 25, lung: 5, dataYear: 2024, confidence: 'estimated',
    summary:
      'Small but fast-growing and well-funded, with emirate-level regulators (DoH Abu Dhabi, DHA) ' +
      'layered on federal MOHAP registration. Distributor model compulsory.',
  },
  {
    iso3: 'DEU', iso2: 'DE', name: 'Germany', population: 84_400_000,
    donorsPmp: 10.9, donorsPmpYear: 2024, donationModel: 'opt_in',
    registry: 'DSO / Eurotransplant', priority: 'tier_2', entryMode: 'distributor',
    kidney: 2200, liver: 800, heart: 350, lung: 300, dataYear: 2024, confidence: 'estimated',
    summary:
      'Largest European market by population but the lowest donation rate in the region. NUB ' +
      'applications to InEK close annually around 31 October and missing it costs a year.',
  },
];

export const SITES: Site[] = [
  // --- Italy: verified from CNT Report 2024 ---
  {
    id: 'ita-padova', name: 'Azienda Ospedale-Università Padova', short: 'Padova',
    city: 'Padova', region: 'Veneto', iso3: 'ITA', lat: 45.4064, lon: 11.8768,
    kinds: ['transplant_centre'], paediatric: true, tier: 1,
    volumes: [
      { organ: 'kidney', year: 2024, total: 217, living: 58, confidence: 'verified' },
      { organ: 'liver', year: 2024, total: 128, living: 3, confidence: 'verified' },
      { organ: 'heart', year: 2024, total: 42, confidence: 'verified' },
      { organ: 'lung', year: 2024, total: 41, confidence: 'verified' },
      { organ: 'pancreas', year: 2024, total: 8, confidence: 'verified' },
    ],
    prevalentGrafts: { value: 2900, confidence: 'estimated', method: '10-year incidence × modelled graft survival' },
    waitingList: 553, hypersensitised: 22, dcdListed: 291, dualKidney: 122,
    note:
      'The single most important Italian centre for molecular diagnostics. Largest living-donor ' +
      'kidney programme in Italy. Highest 10-year volume for kidney, heart, lung and pancreas ' +
      'simultaneously. Italy’s node in international molecular-Banff work and co-owner of the ' +
      'Padova–Verona digital pre-implantation biopsy platform.',
  },
  {
    id: 'ita-torino', name: 'AOU Città della Salute e della Scienza (Molinette)', short: 'Torino Molinette',
    city: 'Torino', region: 'Piemonte', iso3: 'ITA', lat: 45.0416, lon: 7.6760,
    kinds: ['transplant_centre'], tier: 1,
    volumes: [
      { organ: 'kidney', year: 2024, total: 215, living: 7, confidence: 'verified' },
      { organ: 'liver', year: 2024, total: 179, living: 1, confidence: 'verified' },
      { organ: 'heart', year: 2024, total: 28, confidence: 'verified' },
      { organ: 'lung', year: 2024, total: 22, confidence: 'verified' },
      { organ: 'pancreas', year: 2024, total: 2, confidence: 'verified' },
    ],
    prevalentGrafts: { value: 2600, confidence: 'estimated', method: '10-year incidence × modelled graft survival' },
    waitingList: 293, hypersensitised: 12, dcdListed: 98, dualKidney: 34,
    note: 'Italy’s number one centre by total volume (440 procedures) and the only one running all five major organ programmes at scale.',
  },
  {
    id: 'ita-ismett', name: 'IRCCS ISMETT', short: 'ISMETT',
    city: 'Palermo', region: 'Sicilia', iso3: 'ITA', lat: 38.1157, lon: 13.3615,
    kinds: ['transplant_centre'], paediatric: true, tier: 1,
    volumes: [
      { organ: 'kidney', year: 2024, total: 106, living: 19, confidence: 'verified' },
      { organ: 'liver', year: 2024, total: 120, living: 16, confidence: 'verified' },
      { organ: 'heart', year: 2024, total: 40, confidence: 'verified' },
      { organ: 'lung', year: 2024, total: 16, confidence: 'verified' },
    ],
    waitingList: 293, hypersensitised: 19, dcdListed: 153,
    note: 'Fastest-growing Italian centre: kidney +54%, heart +150% in 2024. Dominant paediatric living-donor liver programme. International governance link makes it the most commercially accessible.',
  },
  {
    id: 'ita-bari', name: 'AOU Consorziale Policlinico di Bari', short: 'Bari Policlinico',
    city: 'Bari', region: 'Puglia', iso3: 'ITA', lat: 41.1187, lon: 16.8719,
    kinds: ['transplant_centre'], tier: 1,
    volumes: [
      { organ: 'kidney', year: 2024, total: 89, living: 20, confidence: 'verified' },
      { organ: 'liver', year: 2024, total: 61, confidence: 'verified' },
      { organ: 'heart', year: 2024, total: 73, confidence: 'verified' },
    ],
    waitingList: 361, hypersensitised: 35, dcdListed: 95, dualKidney: 68,
    note:
      'The most dynamic programme in Italy. Heart went 6 → 9 → 26 → 61 → 73 across 2020-24. Largest ' +
      'hypersensitised cohort in the country. Coordinates the consortium that identified urinary ' +
      'CXCL10 and FasL in subclinical rejection, which is directly adjacent to a CXCL9 point-of-care assay.',
  },
  {
    id: 'ita-bologna', name: 'IRCCS AOU di Bologna, Policlinico S. Orsola', short: 'Bologna S. Orsola',
    city: 'Bologna', region: 'Emilia-Romagna', iso3: 'ITA', lat: 44.4949, lon: 11.3426,
    kinds: ['transplant_centre'], tier: 1,
    volumes: [
      { organ: 'kidney', year: 2024, total: 124, living: 33, confidence: 'verified' },
      { organ: 'liver', year: 2024, total: 109, confidence: 'verified' },
      { organ: 'heart', year: 2024, total: 30, confidence: 'verified' },
      { organ: 'lung', year: 2024, total: 5, confidence: 'verified' },
      { organ: 'hsct_allo', year: 2024, total: 58, confidence: 'verified' },
    ],
    waitingList: 562, hypersensitised: 32, dcdListed: 509, dualKidney: 69,
    note: 'Largest DCD-listed cohort in Italy (509) and second-largest hypersensitised (32). That combination makes it the highest-value single site for a graft-injury biomarker.',
  },
  {
    id: 'ita-niguarda', name: 'ASST Grande Ospedale Metropolitano Niguarda', short: 'Niguarda',
    city: 'Milano', region: 'Lombardia', iso3: 'ITA', lat: 45.5088, lon: 9.1889,
    kinds: ['transplant_centre', 'hi_lab'], tier: 2,
    volumes: [
      { organ: 'kidney', year: 2024, total: 106, living: 17, confidence: 'verified' },
      { organ: 'liver', year: 2024, total: 112, confidence: 'verified' },
      { organ: 'heart', year: 2024, total: 35, confidence: 'verified' },
    ],
    waitingList: 173, hypersensitised: 15, dcdListed: 141,
    labNote: 'Vice-President of AIBT is based here.',
    note: 'Attribution warning: some Niguarda-coded activity was performed in convenzione for San Raffaele and Genova, so raw volume overstates its own programme.',
  },
  {
    id: 'ita-bergamo', name: 'ASST Papa Giovanni XXIII', short: 'Bergamo',
    city: 'Bergamo', region: 'Lombardia', iso3: 'ITA', lat: 45.6983, lon: 9.6773,
    kinds: ['transplant_centre'], tier: 2,
    volumes: [
      { organ: 'kidney', year: 2024, total: 58, living: 2, confidence: 'verified' },
      { organ: 'liver', year: 2024, total: 105, confidence: 'verified' },
      { organ: 'heart', year: 2024, total: 22, confidence: 'verified' },
      { organ: 'lung', year: 2024, total: 10, confidence: 'verified' },
      { organ: 'intestine', year: 2024, total: 1, confidence: 'verified' },
      { organ: 'hsct_allo', year: 2024, total: 54, confidence: 'verified' },
    ],
    waitingList: 74, dcdListed: 41,
    note: 'Italy’s only active intestinal transplant centre. Shortest kidney wait in the country. The Remuzzi score that governs Italian pre-implantation biopsy originates from this group.',
  },
  {
    id: 'ita-laquila', name: "Ospedale Civile S. Salvatore", short: "L'Aquila",
    city: "L'Aquila", region: 'Abruzzo', iso3: 'ITA', lat: 42.3498, lon: 13.3995,
    kinds: ['transplant_centre', 'hi_lab', 'reference_lab'], tier: 1,
    volumes: [{ organ: 'kidney', year: 2024, total: 48, living: 6, confidence: 'verified' }],
    waitingList: 193, hypersensitised: 5,
    labNote: 'Centro Regionale Immunoematologia e Tipizzazione Tissutale. Seat of the AIBT presidency.',
    note: 'Strategically important despite modest volume: a national reference immunogenetics laboratory and the political centre of Italian histocompatibility. The right first call in Italy for an HLA typing product.',
  },
  {
    id: 'ita-pisa', name: 'AOU Pisana (Cisanello)', short: 'Pisa',
    city: 'Pisa', region: 'Toscana', iso3: 'ITA', lat: 43.7228, lon: 10.4017,
    kinds: ['transplant_centre'], tier: 2,
    volumes: [
      { organ: 'kidney', year: 2024, total: 42, living: 10, confidence: 'verified' },
      { organ: 'liver', year: 2024, total: 176, confidence: 'verified' },
      { organ: 'pancreas', year: 2024, total: 3, confidence: 'verified' },
    ],
    waitingList: 160, hypersensitised: 19, dualKidney: 40,
    note: 'Italy’s number two liver programme.',
  },
  {
    id: 'ita-torvergata', name: 'AOU Policlinico Tor Vergata', short: 'Tor Vergata',
    city: 'Roma', region: 'Lazio', iso3: 'ITA', lat: 41.8517, lon: 12.6266,
    kinds: ['transplant_centre'], tier: 2,
    volumes: [
      { organ: 'kidney', year: 2024, total: 48, living: 7, confidence: 'verified' },
      { organ: 'liver', year: 2024, total: 29, confidence: 'verified' },
      { organ: 'hsct_allo', year: 2024, total: 64, confidence: 'verified' },
    ],
    note: 'Hosts the Rome Transplant Network, Italy’s largest allogeneic HSCT programme. The primary Italian target for a chimerism assay.',
  },
  {
    id: 'ita-verona', name: 'AOU Integrata Verona (Borgo Trento)', short: 'Verona',
    city: 'Verona', region: 'Veneto', iso3: 'ITA', lat: 45.4479, lon: 10.9903,
    kinds: ['transplant_centre'], tier: 2,
    volumes: [
      { organ: 'kidney', year: 2024, total: 91, living: 11, confidence: 'verified' },
      { organ: 'liver', year: 2024, total: 65, confidence: 'verified' },
      { organ: 'heart', year: 2024, total: 29, confidence: 'verified' },
    ],
    waitingList: 311, dcdListed: 214, dualKidney: 51,
    note: 'Co-owner with Padova of the digital pre-implantation biopsy platform. Note: solid organ is at Borgo Trento; Borgo Roma is the separate HSCT site.',
  },
  {
    id: 'ita-gemelli', name: 'Fondazione Policlinico Universitario A. Gemelli IRCCS', short: 'Gemelli',
    city: 'Roma', region: 'Lazio', iso3: 'ITA', lat: 41.9310, lon: 12.4335,
    kinds: ['transplant_centre'], tier: 2,
    volumes: [
      { organ: 'kidney', year: 2024, total: 65, living: 30, confidence: 'verified' },
      { organ: 'liver', year: 2024, total: 42, confidence: 'verified' },
    ],
    waitingList: 353, dcdListed: 136,
    note: 'Highest living-donor ratio of any large Italian centre at 46%. Living donation means elective, intensive pre-transplant HLA workup.',
  },

];

export const PATHWAYS: Pathway[] = [
  {
    id: 'gbr-hie', iso3: 'GBR', assayClass: 'All assays', routeName: 'Health Institution Exemption (UK MDR 2002 reg. 33(1)(a))',
    routeType: 'none', decisionBody: 'None required',
    howItWorks:
      'Regulation 33(1)(a) disapplies the whole of Part IV, including UKCA marking, from products ' +
      'manufactured and used within the same health institution on the same or immediately adjacent ' +
      'premises without transfer to another legal entity. This is unconditional in Great Britain and ' +
      'materially broader than EU IVDR Article 5(5). An NHS H&I laboratory can therefore validate and ' +
      'run the assay today with no regulatory approval at all.',
    difficulty: 'low', status: 'researching', confidence: 'verified',
    opportunity:
      'CLOSING WINDOW. MHRA ran a call for evidence on conditioning the exemption in late 2025 and on ' +
      '20 January 2026 deleted its enforcement-forbearance wording. Use it to generate UK evidence and ' +
      'reference sites now, but never architect the business model on it: the exemption evaporates the ' +
      'moment a sample crosses to another legal entity.',
  },
  {
    id: 'gbr-testdir', iso3: 'GBR', assayClass: 'dd-cfDNA / chimerism', routeName: 'National Genomic Test Directory (TP401)',
    routeType: 'national_catalogue', decisionBody: 'NHS England Test Evaluation Working Group',
    howItWorks:
      'Anyone, including a manufacturer, may submit an application to add a test or amend a clinical ' +
      'indication. Assessment is on clinical utility, scientific validity, unmet need and patient ' +
      'benefit, followed by a GLH impact assessment and governance approval on an annual cycle. The ' +
      'correct instrument for a pre-launch assay is the Horizon Scanning Notification.',
    submissionWindow: 'Annual cycle; dates currently being revised by NHS England',
    difficulty: 'medium', status: 'not_started', confidence: 'verified',
    opportunity:
      'HIGHEST-LEVERAGE UK OPENING. TP401 "Chimerism Testing, Post Solid Organ Transplant" already ' +
      'exists but contains only STR and XY-FISH. There is no cfDNA method anywhere in the directory, ' +
      'and no HLA test at all. Adding a method to an existing indication is far shorter than creating one.',
  },
  {
    id: 'gbr-scotland', iso3: 'GBR', assayClass: 'All assays', routeName: 'NSD national designation + SHTG assessment',
    routeType: 'hta_appraisal', decisionBody: 'National Specialist Services Committee / Scottish Health Technologies Group',
    howItWorks:
      'National Services Division commissions over 60 designated specialist services through a ' +
      'four-stage process: registration, application, specification and proposal, then a decision by ' +
      'the National Specialist Services Committee followed by Cabinet Secretary approval. Run SHTG in ' +
      'parallel: it explicitly assesses diagnostics, Health Boards are required to consider its ' +
      'Recommendations, and its evidence framework states that a new service may not require a UKCA or CE mark.',
    submissionWindow: 'Registration closes 28 February annually; applications close 31 March',
    nextDeadline: '2027-02-28',
    difficulty: 'medium', status: 'not_started', confidence: 'verified',
    opportunity: 'The fastest formal route in the UK, but the calendar is rigid. Miss 28 February and you wait a year.',
  },
  {
    id: 'usa-clfs', iso3: 'USA', assayClass: 'dd-cfDNA', routeName: 'PLA code → CLFS gapfill → MolDX coverage',
    routeType: 'fee_schedule', decisionBody: 'AMA CPT Editorial Panel / CMS / Palmetto GBA MolDX',
    howItWorks:
      'CLIA certification and in practice New York State CLEP, then an AMA PLA code application on the ' +
      'quarterly cycle, then Medicare pricing on the Clinical Laboratory Fee Schedule by crosswalk or ' +
      'gapfill, then a MolDX technical assessment and Local Coverage Determination. Commercial payers ' +
      'largely follow Medicare.',
    difficulty: 'very_high', status: 'not_started', confidence: 'estimated',
    priceLow: 2500, priceHigh: 2900, currency: 'USD',
    opportunity: 'Slow and evidence-hungry, but it is where the money is. This is the path AlloSure and Prospera walked.',
  },
  {
    id: 'fra-rihn', iso3: 'FRA', assayClass: 'All assays', routeName: 'RIHN (Référentiel des Actes Innovants Hors Nomenclature)',
    routeType: 'innovation_list', decisionBody: 'Ministère de la Santé / HAS',
    howItWorks:
      'RIHN is a national list of innovative out-of-nomenclature assays funded from a ring-fenced ' +
      'envelope, allowing hospitals to claim for a test before it reaches the NABM nomenclature. After ' +
      'evidence accrual and HAS evaluation, a test migrates from RIHN to NABM for permanent funding.',
    submissionWindow: 'Annual cycle',
    difficulty: 'medium', status: 'not_started', confidence: 'estimated',
    opportunity: 'The most useful early-access route in Europe and the one most companies miss. Worth designing a European entry sequence around.',
  },
  {
    id: 'deu-nub', iso3: 'DEU', assayClass: 'All assays', routeName: 'NUB status (Neue Untersuchungs- und Behandlungsmethoden)',
    routeType: 'drg_addon', decisionBody: 'InEK',
    howItWorks:
      'Hospitals apply annually to InEK for NUB status for a new method not yet covered by the G-DRG ' +
      'system. NUB status permits a hospital to negotiate an individual additional payment with its ' +
      'sickness funds. It is the hospital, not the manufacturer, that applies, so the commercial task ' +
      'is to equip a hospital to file.',
    submissionWindow: 'Annual, typically closing 31 October',
    nextDeadline: '2026-10-31',
    difficulty: 'high', status: 'not_started', confidence: 'estimated',
    opportunity: 'A hard annual deadline weeks away. Missing 31 October 2026 costs a full year of German inpatient revenue.',
  },
  {
    id: 'ita-regional', iso3: 'ITA', assayClass: 'All assays', routeName: 'LEA listing then 20 regional tariffs',
    routeType: 'national_catalogue', decisionBody: 'Ministero della Salute, then each Regione',
    howItWorks:
      'A test must first sit within the Livelli Essenziali di Assistenza outpatient specialist ' +
      'nomenclature. National inclusion does not by itself produce payment: each of 20 regions then ' +
      'sets its own tariff, and regions differ on both whether and how much they pay.',
    difficulty: 'high', status: 'not_started', confidence: 'unverified',
    opportunity: 'Market entry in Italy is effectively a 20-market exercise. In practice the regional tender is the sales process.',
  },
  {
    id: 'sau-nupco', iso3: 'SAU', assayClass: 'All assays', routeName: 'SFDA registration then NUPCO tender',
    routeType: 'tender', decisionBody: 'Saudi FDA / NUPCO',
    howItWorks:
      'An IVD requires SFDA Medical Device Marketing Authorisation held through an Authorised ' +
      'Representative, after which central government procurement runs through NUPCO. You generally ' +
      'cannot sell direct: a local agent or distributor is compulsory.',
    difficulty: 'high', status: 'not_started', confidence: 'estimated',
    opportunity: 'Distributor selection is the whole game, and anti-bribery due diligence on that distributor is a legal requirement under the UK Bribery Act, not a nicety.',
  },
];

/* --- Derived helpers ------------------------------------------------------------- */

export function countryOf(iso3: string): Country | undefined {
  return COUNTRIES.find((c) => c.iso3 === iso3);
}


/* =====================================================================================
   Merge with the generated research seed.

   Hand-curated entries above win on collision: they carry richer commercial assessment
   and verified coordinates. Everything else comes from the consolidated research pass.
   ===================================================================================== */

import { GEN_SITES, GEN_COUNTRIES, GEN_PATHWAYS } from './atlas.generated';
import { UK_SITES } from './atlas.uk';
import { AMERICAS_SITES, AMERICAS_COUNTRIES, AMERICAS_PATHWAYS } from './atlas.americas';
import { WAITING } from './atlas.waiting';

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

/* Exact-string dedup is not enough: "S. Orsola" and "Sant'Orsola", or "QEHB" and
   "Queen Elizabeth Hospital Birmingham", are the same institution written two ways.
   Match on distinctive tokens within the same city instead. */
const NOISE = new Set([
  'hospital','hospitals','university','universitario','universitari','universitaire','nhs','foundation',
  'trust','the','of','and','de','del','della','di','du','des','la','le','el','los','las','centre','center',
  'medical','health','general','royal','saint','st','sant','san','santa','campus','ltd','ospedale','azienda',
  'aou','asst','irccs','chu','aphp','klinikum','ziekenhuis','umc','complejo','infirmary','institute',
  'fondazione','fundacion','regional','national','clinic','clinical','policlinico','ospedaliero',
  'hopital','hopitaux','hospices','civils','universitaires','universitaire','service','services',
  'departement','department','dipartimento','nephrologie','nephrology','nefrologia','transplantation',
  'transplant','trapianti','trasplante','chirurgie','surgery',
  'hastanesi','universitesi','fakultesi','merkezi','nakli','organ','ziekenhuis','academisch','krankenhaus',
  'klinik','kliniken','universitatsklinikum','universitatsmedizin','sjukhus','universitetssjukhuset',
  'szpital','instytut','nemocnice','fakultni','spitalul','hospitalario','sanitario','assistenziale',
  'centro','centrum','unidad','unita','servizio','laboratoire','laboratorio','laboratory','immunologie',
  'immunology','histocompatibilite','histocompatibility','immunogenetics','immunogenetica','renale',
  'renal','kidney','liver','heart','lung',
]);
const toks = (s: string) =>
  new Set(
    s.toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .split(/[^a-z0-9]+/)
      .filter((t) => t.length > 2 && !NOISE.has(t)),
  );

function sameInstitution(a: Site, b: Site): boolean {
  if (a.iso3 !== b.iso3) return false;
  if (norm(a.name) === norm(b.name) || norm(a.short) === norm(b.short)) return true;
  if (norm(a.city) !== norm(b.city)) return false;

  // A children's hospital is a different institution and a different programme from the
  // adult hospital in the same city, however similar the names look.
  const paed = (s: string) => /child|paediatr|pediatr|enfant|infantil|bambin|kinder/i.test(s);
  if (paed(a.name + a.short) !== paed(b.name + b.short)) return false;

  const at = new Set([...toks(a.name), ...toks(a.short)]);
  const bt = new Set([...toks(b.name), ...toks(b.short)]);
  if (!at.size || !bt.size) return false;

  const shared = [...at].filter((t) => bt.has(t));
  if (!shared.length) return false;

  // One name being a subset of the other means the same institution written short and
  // long ("QEHB" inside "Queen Elizabeth Hospital Birmingham").
  if (shared.length === at.size || shared.length === bt.size) return true;

  // Otherwise require genuine overlap. Two campuses of one health system
  // (NewYork-Presbyterian Weill Cornell vs Columbia) share the system name but each
  // carries distinctive tokens the other lacks, and are separate transplant programmes.
  const union = new Set([...at, ...bt]).size;
  return shared.length / union >= 0.55;
}

const curated: Site[] = [
  ...SITES.map((s) => ({ ...s, geo: 'city' as const, source: 'curated' })),
  ...UK_SITES,
  ...AMERICAS_SITES,
];

// Two records carrying the same id are the same institution by construction, whatever their
// names look like. The name matcher scores "AOU Consorziale Policlinico di Bari" against
// "Azienda Ospedaliero Universitaria Policlinico di Bari" at 2 shared tokens out of 7, which
// is 0.29 and well under the threshold, so both survive sameInstitution. The id is
// conclusive where the name is not, and this is the third instance of the duplicate class
// after Birmingham and Bologna, so it gets a structural guard rather than another rule.
function mergeSites(a: Site, b: Site): Site {
  const vols = [...(a.volumes ?? [])];
  for (const v of b.volumes ?? []) {
    const hit = vols.findIndex((x) => x.organ === v.organ);
    if (hit === -1) vols.push(v);
    // Same organ from two sources: the more recent reporting year wins. Never sum them,
    // which would double-count a centre's activity.
    else if ((v.year ?? 0) > (vols[hit].year ?? 0)) vols[hit] = v;
  }
  return {
    ...b,
    ...a, // the curated record wins on identity fields
    kinds: [...new Set([...(a.kinds ?? []), ...(b.kinds ?? [])])],
    paediatric: Boolean(a.paediatric || b.paediatric),
    volumes: vols,
    prevalentGrafts: a.prevalentGrafts ?? b.prevalentGrafts,
    waitingList: a.waitingList ?? b.waitingList,
  };
}

/* =====================================================================================
   PREVALENT GRAFTS

   The number of people walking around with a functioning graft, which is the addressable
   base for a surveillance assay. It is NOT the transplant rate: registries publish incidence
   because that is what they count, and using it as the market size understates it roughly
   tenfold.

   Before this, 497 of 617 centres carried no figure at all, including every centre in the
   United States and Germany. A map of the transplant world with a zero over America is not
   a market model.

   METHOD. Each organ has a multiplier: how many years of current transplant volume are
   walking around at any moment. That is not the same as mean graft survival, because volumes
   have grown, so the back years contributed fewer grafts than today's rate implies.

   The kidney multiplier is CALIBRATED, not assumed. USRDS reports 273,041 people living
   with a functioning kidney transplant in the United States at the end of 2024, against
   28,142 kidney transplants in 2023. 273,041 / 28,142 = 9.70. Using the ~12-year restricted
   mean graft survival from the survival curves instead would overstate the US base by 24%.

   The other organs have no equivalent published anchor I could verify, so they are modelled
   from published graft survival and marked 'estimated' rather than 'derived'. They are
   honest approximations and should be treated as such. Kidney is the one that matters most
   for dd-cfDNA surveillance and it is the one with a real number behind it.
   ===================================================================================== */

function derivePrevalent(s: Site): Site {
  // A verified, registry-reported figure always wins over arithmetic.
  if (s.prevalentGrafts?.confidence === 'verified') return s;

  const byOrgan: Record<string, number> = {};
  let total = 0;
  let anchored = false;

  for (const v of s.volumes ?? []) {
    const years = GRAFT_YEARS[v.organ];
    if (!years || !v.total) continue;
    const n = Math.round(v.total * years);
    byOrgan[v.organ] = (byOrgan[v.organ] ?? 0) + n;
    total += n;
    if (v.organ === 'kidney' || v.organ === 'kidney_pancreas') anchored = true;
  }

  if (!total) return s;   // no volumes, so nothing honest to say

  return {
    ...s,
    prevalentGrafts: {
      value: total,
      // 'derived' only where the calibrated kidney multiplier carries the number. A
      // liver-only centre is modelled from survival literature with no anchor behind it,
      // and the map must not present those two as equally solid.
      confidence: anchored ? 'derived' : 'estimated',
      method:
        'Annual transplant volume by organ multiplied by organ-specific graft-years. ' +
        'Kidney multiplier 8.44, calibrated so the 254 US centres reproduce the USRDS ' +
        'published figure of 273,041 functioning kidney grafts at end-2024. Other organs ' +
        'modelled from graft survival and marked estimated. Assumes a centre has held a ' +
        'stable share of national activity, which field intelligence should correct.',
    },
    prevalentByOrgan: byOrgan,
  };
}

const merged = new Map<string, Site>();
for (const s of [
  ...curated,
  ...(GEN_SITES as unknown as Site[]).filter((g) => !curated.some((c) => sameInstitution(c, g))),
]) {
  const prior = merged.get(s.id);
  merged.set(s.id, prior ? mergeSites(prior, s) : s);
}

export const ALL_SITES: Site[] = [...merged.values()].map(derivePrevalent);

const curatedCountries = new Set(COUNTRIES.map((c) => c.iso3));

/* Waiting lists are attached here rather than carried in each country literal, so the
   four-month recheck regenerates one file instead of editing three hand-curated seeds. */
const withWaiting = (c: Country): Country =>
  WAITING[c.iso3] ? { ...c, waiting: WAITING[c.iso3] } : c;

export const ALL_COUNTRIES: Country[] = ([
  ...COUNTRIES,
  ...AMERICAS_COUNTRIES,
  ...(GEN_COUNTRIES as unknown as Country[])
    .filter((c) => !curatedCountries.has(c.iso3))
    .map((c) => ({
      ...c,
      priority: (c.priority ?? 'tier_3') as Country['priority'],
      entryMode: (c.entryMode ?? 'distributor') as Country['entryMode'],
      iso2: c.iso2 ?? '',
      kidney: c.kidney ?? null,
      dataYear: c.dataYear ?? 0,
      confidence: (c.confidence ?? 'estimated') as Confidence,
    })),
].map(withWaiting) as Country[]);

const curatedPathwayKeys = new Set(PATHWAYS.map((p) => `${p.iso3}|${norm(p.routeName)}`));

export const ALL_PATHWAYS: Pathway[] = [
  ...PATHWAYS,
  ...AMERICAS_PATHWAYS,
  ...(GEN_PATHWAYS as unknown as Pathway[])
    .filter((p) => p.iso3 && !curatedPathwayKeys.has(`${p.iso3}|${norm(p.routeName)}`))
    .map((p) => ({
      ...p,
      status: (p.status ?? 'not_started') as Pathway['status'],
      confidence: (p.confidence ?? 'estimated') as Confidence,
      difficulty: (p.difficulty ?? 'medium') as Pathway['difficulty'],
    })),
];
